import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { RootState } from "@store/index";
import { trpc } from "@utils/api";
import type {
  ModelDefinition as SkedyulModelDefinition,
  NavigationConfig as SkedyulNavigationConfig,
  PageDefinition as SkedyulPageDefinition,
} from "skedyul-node";
import {
  emptyAsyncPanel,
  fetchCrmMapOverview,
  fetchCrmSetupStatus,
  fetchEventWiringOverview,
  fetchInstallSetupSteps,
  fetchLatestBatchJob,
  initialInstallPanelsState,
  refreshInstallPanels,
  type BatchOperationJobData,
  type InstallPanelsState,
} from "./appResourcePagePanels";
import { mergeRenderedPageBlock } from "./overlayRenderedPageBlock";

export {
  fetchCrmMapOverview,
  fetchCrmSetupStatus,
  fetchEventWiringOverview,
  fetchInstallSetupSteps,
  fetchLatestBatchJob,
  refreshInstallPanels,
  eventWiringCacheKey,
  batchJobCacheKey,
  type AppCrmMapOverviewData,
  type EventWiringOverviewData,
  type InstallSetupStepsData,
  type CrmSetupStatusValue,
  type BatchOperationJobData,
  type InstallPanelsState,
} from "./appResourcePagePanels";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

// Note: Web app uses uppercase conventions (INSTANCE, LIST, INTERNAL, SHARED)
// while skedyul-node uses lowercase. These types are web-specific adaptations
// of the skedyul-node types for UI rendering.

// Page field source for data binding
export type PageFieldSource = {
  model: string;
  field: string;
};

// Header for FORM type fields
export type PageFormHeader = {
  title: string;
  description?: string;
};

// Self-contained page field definition
export type PageFieldDef = {
  handle: string;
  type:
    | "STRING"
    | "FILE"
    | "NUMBER"
    | "DATE"
    | "BOOLEAN"
    | "SELECT"
    | "FORM"
    | "RELATIONSHIP"
    | "ALERT";
  label: string;
  description?: string;
  required?: boolean;
  handler?: string;
  source?: PageFieldSource;
  options?: Array<{ value: string; label: string }>;
  accept?: string;
  header?: PageFormHeader;
  fields?: PageFieldDef[];
  actions?: PageActionDef[];
  model?: string;
  alertIcon?: string;
  alertVariant?: "default" | "destructive";
  hidden?: boolean;
};

// Page action definition
export type PageActionDef = {
  handle: string;
  label: string;
  handler: string;
  icon?: string;
  variant?: "primary" | "secondary" | "destructive";
  isDisabled?: boolean | string;
  isHidden?: boolean | string;
  /** SDK form actions (`disabled` / `hidden`) — boolean or Liquid, resolved before render. */
  disabled?: boolean | string;
  hidden?: boolean | string;
};

// FormV2 Component Types
export type FormV2StyleProps = {
  id: string;
  row: number;
  col: number;
  className?: string;
  hidden?: boolean;
};

export type RelationshipExtension = {
  model: string;
};

export type FieldSettingButtonProps = {
  label: string;
  variant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
  size?: "default" | "sm" | "lg" | "icon";
  isLoading?: boolean;
  isDisabled?: boolean | string;
  /** SDK button prop. Liquid resolves to "true" / "false". */
  disabled?: boolean | string;
  leftIcon?: string;
};

export type FormLayoutColumnDef = {
  field: string;
  colSpan: number;
  dataType?: string;
  subQuery?: unknown;
};

export type FormLayoutRowDef = {
  columns: FormLayoutColumnDef[];
};

export type FormLayoutConfigDef = {
  type: "form";
  rows: FormLayoutRowDef[];
};

export type ModalFormDef = {
  header?: PageFormHeader;
  handler?: string;
  fields?: FormV2ComponentDef[];
  layout?: FormLayoutConfigDef;
  actions?: PageActionDef[];
  /** Named dialog template to use instead of inline fields */
  template?: string;
  /** Parameters to pass to the named dialog template */
  templateParams?: Record<string, unknown>;
};

export type InputComponentDef = FormV2StyleProps & {
  component: "Input";
  props: {
    label?: string;
    placeholder?: string;
    description?: string;
    helpText?: string;
    type?: "text" | "number" | "email" | "password" | "tel" | "url" | "hidden";
    required?: boolean;
    disabled?: boolean;
    value?: string | number;
  };
};

export type TextareaComponentDef = FormV2StyleProps & {
  component: "Textarea";
  props: {
    label?: string;
    placeholder?: string;
    description?: string;
    helpText?: string;
    required?: boolean;
    disabled?: boolean;
    value?: string;
  };
};

export type MarkdownComponentDef = FormV2StyleProps & {
  component: "Markdown";
  props?: {
    label?: string;
    placeholder?: string;
    description?: string;
    helpText?: string;
    required?: boolean;
    disabled?: boolean;
    isDisabled?: boolean;
    value?: string;
  };
};

export type SelectComponentDef = FormV2StyleProps & {
  component: "Select";
  props: {
    label?: string;
    placeholder?: string;
    description?: string;
    helpText?: string;
    items?: Array<{ value: string; label: string }>;
    value?: string;
    isDisabled?: boolean;
    required?: boolean;
  };
  relationship?: RelationshipExtension;
};

export type ComboboxComponentDef = FormV2StyleProps & {
  component: "Combobox";
  props: {
    label?: string;
    placeholder?: string;
    description?: string;
    helpText?: string;
    items?: Array<{ value: string; label: string }>;
    value?: string;
    required?: boolean;
  };
  relationship?: RelationshipExtension;
};

export type CheckboxComponentDef = FormV2StyleProps & {
  component: "Checkbox";
  props: {
    label?: string;
    description?: string;
    helpText?: string;
    checked?: boolean;
    disabled?: boolean;
    required?: boolean;
  };
};

export type FieldSettingComponentDef = FormV2StyleProps & {
  component: "FieldSetting";
  props: {
    label: string;
    description?: string;
    helpText?: string;
    mode?: "field" | "setting";
    button: FieldSettingButtonProps;
  };
  modalForm?: ModalFormDef;
};

export type ImageSettingComponentDef = FormV2StyleProps & {
  component: "ImageSetting";
  props: {
    label?: string;
    description?: string;
    helpText?: string;
    accept?: string;
    required?: boolean;
  };
};

export type FileSettingComponentDef = FormV2StyleProps & {
  component: "FileSetting";
  props: {
    label?: string;
    description?: string;
    helpText?: string;
    accept?: string;
    required?: boolean;
    button?: {
      label?: string;
      variant?: "default" | "outline" | "ghost" | "link";
      size?: "sm" | "md" | "lg";
    };
  };
};

export type EmptyFormComponentDef = FormV2StyleProps & {
  component: "EmptyForm";
  props: {
    title?: string;
    description?: string;
    icon?: string;
  };
};

export type ListComponentDef = FormV2StyleProps & {
  component: "List";
  model: string;
  labelField?: string;
  descriptionField?: string;
  icon?: string;
  emptyMessage?: string;
  props?: {
    title?: string;
    description?: string;
  };
};

export type AlertComponentDef = FormV2StyleProps & {
  component: "Alert";
  props: {
    title: string;
    description: string;
    icon?: string;
    variant?: "default" | "destructive";
  };
};

export type EventWiringPanelComponentDef = FormV2StyleProps & {
  component: "EventWiringPanel";
  props: {
    eventTypes: Array<{
      type: string;
      label: string;
      sourceType?: string;
      description?: string;
      icon?: string;
      direction?: "inbound" | "outbound";
      recommendedWorkflowHandle?: string;
    }>;
    recommendedWorkflowHandle?: string;
  };
};

export type EntityCrmMapStatusComponentDef = FormV2StyleProps & {
  component: "EntityCrmMapStatus";
  props: {
    entity: string;
    href: string;
    title?: string;
    description?: string;
  };
};

export type BatchOperationPanelComponentDef = FormV2StyleProps & {
  component: "BatchOperationPanel";
  props: {
    entity: string;
    operationHandle: string;
    title?: string;
    description?: string;
    buttonLabel?: string;
    icon?: string;
  };
};

export type EntityCrmMapEditorComponentDef = FormV2StyleProps & {
  component: "EntityCrmMapEditor";
  props: {
    entity: string;
    section?: "overview" | "fields" | "relationships";
  };
};

export type CrmInstallSetupConsentComponentDef = FormV2StyleProps & {
  component: "CrmInstallSetupConsent";
  props?: {
    appName?: string;
  };
};

export type CrmSetupStatusBannerComponentDef = FormV2StyleProps & {
  component: "CrmSetupStatusBanner";
  props?: {
    setupHref?: string;
  };
};

export type WorkflowSetupPlaceholderComponentDef = FormV2StyleProps & {
  component: "WorkflowSetupPlaceholder";
  props?: {
    appName?: string;
  };
};

export type InstallSetupPanelComponentDef = FormV2StyleProps & {
  component: "InstallSetupPanel";
  props?: Record<string, unknown>;
};

export type SecretInputComponentDef = FormV2StyleProps & {
  component: "SecretInput";
  props?: {
    label?: string;
    description?: string;
    helpText?: string;
    value?: string;
    lastFour?: string | null;
    visible?: boolean;
    envVariableKey?: string;
    envVariableId?: string;
    required?: boolean;
    disabled?: boolean;
  };
};

export type FormV2ComponentDef =
  | InputComponentDef
  | TextareaComponentDef
  | MarkdownComponentDef
  | SelectComponentDef
  | ComboboxComponentDef
  | CheckboxComponentDef
  | FieldSettingComponentDef
  | ImageSettingComponentDef
  | FileSettingComponentDef
  | EmptyFormComponentDef
  | ListComponentDef
  | AlertComponentDef
  | EventWiringPanelComponentDef
  | EntityCrmMapStatusComponentDef
  | BatchOperationPanelComponentDef
  | EntityCrmMapEditorComponentDef
  | CrmInstallSetupConsentComponentDef
  | CrmSetupStatusBannerComponentDef
  | WorkflowSetupPlaceholderComponentDef
  | InstallSetupPanelComponentDef
  | SecretInputComponentDef;

export type FormV2PropsDef = {
  formVersion: "v2";
  id?: string;
  fields: FormV2ComponentDef[];
  layout: FormLayoutConfigDef;
  /** Optional actions that trigger MCP tool calls */
  actions?: PageActionDef[];
};

export type CardBlockHeader = {
  title: string;
  description?: string;
  descriptionHref?: string;
};

export type CardBlockDef = {
  type: "card";
  restructurable?: boolean;
  header?: CardBlockHeader;
  form: FormV2PropsDef;
  actions?: PageActionDef[];
  secondaryActions?: PageActionDef[];
  primaryActions?: PageActionDef[];
};

export type LegacyFormBlockDef = {
  type: "form" | "spreadsheet" | "kanban" | "calendar" | "link";
  title?: string;
  fields?: PageFieldDef[];
  readonly?: boolean;
};

export type ListBlockDef = {
  type: "list";
  title?: string;
  model: string;
  labelField?: string;
  descriptionField?: string;
  icon?: string;
  emptyMessage?: string;
};

export type PageBlockDef =
  | CardBlockDef
  | LegacyFormBlockDef
  | ListBlockDef;

export type PageInstanceFilter = {
  model: string;
  where?: Record<string, unknown>;
};

export type PageContextMode = "first" | "many" | "count";

export type PageContextItemDef = {
  model: string;
  mode: PageContextMode;
  filters?: Record<string, Record<string, unknown>>;
  limit?: number;
};

export type PageContextDef = Record<string, PageContextItemDef>;

/**
 * Page definition - re-exported from skedyul-node.
 * Uses lowercase type values: 'instance' | 'list'
 */
export type PageDef = SkedyulPageDefinition;

/**
 * Model definition - re-exported from skedyul-node.
 * Uses lowercase scope values: 'internal' | 'shared'
 */
export type InternalModelDef = SkedyulModelDefinition;

export type ModelInstance = {
  id: string;
  data: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

/**
 * Rendered navigation structure.
 * Same shape as skedyul-node's NavigationConfig after Liquid template rendering.
 */
export type RenderedNavigation = SkedyulNavigationConfig;

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

export function matchPagePath(
  urlPath: string,
  pagePath: string,
): { matched: boolean; params: Record<string, string> } | null {
  const urlSegments = urlPath
    .replace(/^\/|\/$/g, "")
    .split("/")
    .filter(Boolean);
  const pageSegments = pagePath
    .replace(/^\/|\/$/g, "")
    .split("/")
    .filter(Boolean);

  if (urlSegments.length !== pageSegments.length) {
    return null;
  }

  const extractedParams: Record<string, string> = {};

  for (let i = 0; i < pageSegments.length; i++) {
    const pageSegment = pageSegments[i];
    const urlSegment = urlSegments[i];

    if (!pageSegment || !urlSegment) {
      return null;
    }

    const dynamicMatch = pageSegment.match(/^\[([^\]]+)\]$/);
    if (dynamicMatch?.[1]) {
      extractedParams[dynamicMatch[1]] = urlSegment;
    } else {
      if (pageSegment !== urlSegment) {
        return null;
      }
    }
  }

  return { matched: true, params: extractedParams };
}

export function findPageByPath(
  pages: PageDef[],
  urlPath: string,
): { page: PageDef; pathParams: Record<string, string> } | null {
  for (const page of pages) {
    const match = matchPagePath(urlPath, page.path);
    if (match?.matched) {
      return { page, pathParams: match.params };
    }
  }
  return null;
}

export function getPrimaryModelHandle(
  pageDef: PageDef | null,
): string | undefined {
  if (!pageDef) return undefined;

  if (pageDef.context) {
    const firstEntry = Object.values(pageDef.context)[0];
    if (firstEntry && "model" in firstEntry && firstEntry.model) {
      return firstEntry.model as string;
    }
  }

  return pageDef.filter?.model;
}

// Type guards for block types
export function isCardBlock(block: unknown): block is CardBlockDef {
  if (!block || typeof block !== "object") return false;
  const b = block as Record<string, unknown>;
  const typeMatch =
    typeof b.type === "string" && b.type.toLowerCase() === "card";
  const formMatch =
    b.form &&
    typeof b.form === "object" &&
    (b.form as Record<string, unknown>).formVersion === "v2";
  return typeMatch || Boolean(formMatch);
}

export function isListBlock(block: unknown): block is ListBlockDef {
  if (!block || typeof block !== "object") return false;
  const b = block as Record<string, unknown>;
  return b.type === "list" && typeof b.model === "string";
}

export function isLegacyFormBlock(block: unknown): block is LegacyFormBlockDef {
  if (!block || typeof block !== "object") return false;
  const b = block as Record<string, unknown>;
  return ["form", "spreadsheet", "kanban", "calendar", "link"].includes(
    b.type as string,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// State Interface
// ─────────────────────────────────────────────────────────────────────────────

export interface AppResourcePageState {
  // Current URL path
  urlPath: string;

  // Page definition from config
  pageDef: PageDef | null;
  pathParams: Record<string, string>;
  modelDef: InternalModelDef | null;

  // Rendered page (Liquid resolved)
  renderedPageDef: PageDef | null;

  // Form state
  formData: Record<string, unknown>;
  fileDisplayNames: Record<string, string>;

  // Related data
  relationshipOptions: Record<string, Array<{ value: string; label: string }>>;
  listBlockInstances: Record<string, ModelInstance[]>;

  // Navigation
  pageNavigation: RenderedNavigation | null;

  // Loading states
  isLoading: boolean;
  isRenderedConfigLoading: boolean;
  actionLoadingStates: Record<string, boolean>;

  // Bumped when install panels refresh so controlled UI can sync
  panelRefreshKey: number;

  // Special FormV2 panel data (CRM map, event wiring, setup, batch jobs)
  panels: InstallPanelsState;

  // Fetch cache to prevent duplicate requests
  fetchCacheKey: string | null;
}

const initialState: AppResourcePageState = {
  urlPath: "",
  pageDef: null,
  pathParams: {},
  modelDef: null,
  renderedPageDef: null,
  formData: {},
  fileDisplayNames: {},
  relationshipOptions: {},
  listBlockInstances: {},
  pageNavigation: null,
  isLoading: true,
  isRenderedConfigLoading: true,
  actionLoadingStates: {},
  panelRefreshKey: 0,
  panels: initialInstallPanelsState,
  fetchCacheKey: null,
};

// ─────────────────────────────────────────────────────────────────────────────
// Async Thunks
// ─────────────────────────────────────────────────────────────────────────────

type GetResourcePageInput = {
  appInstallationId: string;
  pagePath: string;
  pathParams: Record<string, string>;
};

type GetResourcePageResponse = {
  page: {
    type: "INSTANCE" | "LIST";
    title: string;
    path: string;
    navigation?: boolean;
    blocks?: unknown[];
    actions?: Array<{
      handle: string;
      label: string;
      handler: string;
      icon?: string;
      variant?: "primary" | "secondary" | "destructive";
      isDisabled?: boolean;
      isHidden?: boolean;
    }>;
    filter?: {
      model: string;
      where?: Record<string, unknown>;
    };
  } | null;
  navigation: RenderedNavigation;
};

export const getResourcePage = createAsyncThunk<
  { page: GetResourcePageResponse; pageDef: PageDef | null },
  GetResourcePageInput,
  { state: RootState }
>("appResourcePage/getResourcePage", async (input, { getState }) => {
  const state = getState();
  const pageDef = state.appResourcePage.pageDef;

  const result = await (
    trpc.apps.getPage as unknown as {
      query: (input: GetResourcePageInput) => Promise<GetResourcePageResponse>;
    }
  ).query(input);

  return { page: result, pageDef };
});

type GetRelationshipOptionsInput = {
  appVersionId: string;
  installId: string;
  fields: Array<{ handle: string; model: string }>;
};

export const getRelationshipOptions = createAsyncThunk<
  Record<string, Array<{ value: string; label: string }>>,
  GetRelationshipOptionsInput
>("appResourcePage/getRelationshipOptions", async (input) => {
  const newOptions: Record<
    string,
    Array<{ value: string; label: string }>
  > = {};

  for (const field of input.fields) {
    if (!field.model) continue;
    try {
      const result = await (
        trpc.apps.getInternalModelInstances as unknown as {
          query: (input: {
            appVersionId: string;
            modelHandle: string;
            filter?: Record<string, unknown>;
          }) => Promise<{
            data: Array<{
              id: string;
              _meta?: { label?: string };
              [key: string]: unknown;
            }>;
          }>;
        }
      ).query({
        appVersionId: input.appVersionId,
        modelHandle: field.model,
        filter: { appInstallationId: input.installId },
      });

      newOptions[field.handle] = (result?.data ?? []).map((inst) => {
        const label = inst._meta?.label ?? inst.id;
        return { value: inst.id, label };
      });
    } catch (error) {
      console.error(
        `Failed to fetch relationship options for ${field.handle}:`,
        error,
      );
      newOptions[field.handle] = [];
    }
  }

  return newOptions;
});

type GetListBlockInstancesInput = {
  appVersionId: string;
  installId: string;
  modelHandles: string[];
};

export const getListBlockInstances = createAsyncThunk<
  Record<string, ModelInstance[]>,
  GetListBlockInstancesInput
>("appResourcePage/getListBlockInstances", async (input) => {
  const newInstances: Record<string, ModelInstance[]> = {};

  for (const modelHandle of input.modelHandles) {
    try {
      const result = await (
        trpc.apps.getInternalModelInstances as unknown as {
          query: (input: {
            appVersionId: string;
            modelHandle: string;
            filter?: Record<string, unknown>;
          }) => Promise<{
            instances: ModelInstance[];
          }>;
        }
      ).query({
        appVersionId: input.appVersionId,
        modelHandle,
        filter: { _installation: input.installId },
      });
      newInstances[modelHandle] = result.instances ?? [];
    } catch (error) {
      console.error(
        `Failed to fetch instances for list model ${modelHandle}:`,
        error,
      );
      newInstances[modelHandle] = [];
    }
  }

  return newInstances;
});

// ─────────────────────────────────────────────────────────────────────────────
// Slice
// ─────────────────────────────────────────────────────────────────────────────

export const appResourcePageSlice = createSlice({
  name: "appResourcePage",
  initialState,
  reducers: {
    setUrlPath(state, action: PayloadAction<string>) {
      state.urlPath = action.payload;
    },
    setPageDef(
      state,
      action: PayloadAction<{
        pageDef: PageDef | null;
        pathParams: Record<string, string>;
        modelDef?: InternalModelDef | null;
      }>,
    ) {
      state.pageDef = action.payload.pageDef;
      state.pathParams = action.payload.pathParams;
      state.modelDef = action.payload.modelDef ?? null;
      state.isLoading = false;
    },
    updateFormData(state, action: PayloadAction<Record<string, unknown>>) {
      state.formData = { ...state.formData, ...action.payload };
    },
    updateFormField(
      state,
      action: PayloadAction<{ handle: string; value: unknown }>,
    ) {
      state.formData[action.payload.handle] = action.payload.value;
    },
    setFileDisplayName(
      state,
      action: PayloadAction<{ handle: string; name: string }>,
    ) {
      state.fileDisplayNames[action.payload.handle] = action.payload.name;
    },
    setRelationshipOptions(
      state,
      action: PayloadAction<
        Record<string, Array<{ value: string; label: string }>>
      >,
    ) {
      state.relationshipOptions = action.payload;
    },
    setListBlockInstances(
      state,
      action: PayloadAction<Record<string, ModelInstance[]>>,
    ) {
      state.listBlockInstances = action.payload;
    },
    setPageNavigation(state, action: PayloadAction<RenderedNavigation | null>) {
      state.pageNavigation = action.payload;
    },
    setFetchCacheKey(state, action: PayloadAction<string | null>) {
      state.fetchCacheKey = action.payload;
    },
    setIsRenderedConfigLoading(state, action: PayloadAction<boolean>) {
      state.isRenderedConfigLoading = action.payload;
    },
    setActionLoadingState(
      state,
      action: PayloadAction<{ handle: string; loading: boolean }>,
    ) {
      state.actionLoadingStates[action.payload.handle] =
        action.payload.loading;
    },
    bumpPanelRefreshKey(state) {
      state.panelRefreshKey += 1;
    },
    clearInstallPanels(state) {
      state.panels = initialInstallPanelsState;
      state.panelRefreshKey = 0;
    },
    applyBatchJobRealtimeUpdate(
      state,
      action: PayloadAction<{
        batchJobId: string;
        status?: string;
        progress?: NonNullable<BatchOperationJobData["meta"]>["progress"];
        progressByEntity?: NonNullable<
          BatchOperationJobData["meta"]
        >["progressByEntity"];
        sampleFailures?: NonNullable<
          BatchOperationJobData["meta"]
        >["sampleFailures"];
        errorTallies?: NonNullable<
          BatchOperationJobData["meta"]
        >["errorTallies"];
        error?: string;
        complete?: boolean;
      }>,
    ) {
      const payload = action.payload;
      const statusMap: Record<string, BatchOperationJobData["status"]> = {
        pending: "PENDING",
        running: "RUNNING",
        completed: "COMPLETED",
        failed: "FAILED",
        cancelled: "CANCELLED",
      };
      const nextStatus = payload.status
        ? statusMap[payload.status.toLowerCase()]
        : undefined;

      for (const [key, entry] of Object.entries(state.panels.batchJobsByKey)) {
        if (!entry.data || entry.data.id !== payload.batchJobId) continue;
        const prevMeta = entry.data.meta;
        state.panels.batchJobsByKey[key] = {
          ...entry,
          data: {
            ...entry.data,
            status: nextStatus ?? entry.data.status,
            meta: prevMeta
              ? {
                  ...prevMeta,
                  progress: payload.progress ?? prevMeta.progress,
                  progressByEntity:
                    payload.progressByEntity ?? prevMeta.progressByEntity,
                  sampleFailures:
                    payload.sampleFailures ?? prevMeta.sampleFailures,
                  errorTallies: payload.errorTallies ?? prevMeta.errorTallies,
                  error: payload.error ?? prevMeta.error,
                  completedAt:
                    payload.complete ||
                    nextStatus === "COMPLETED" ||
                    nextStatus === "FAILED" ||
                    nextStatus === "CANCELLED"
                      ? (prevMeta.completedAt ?? new Date().toISOString())
                      : prevMeta.completedAt,
                }
              : prevMeta,
          },
          status: "succeeded",
        };
        state.panelRefreshKey += 1;
        break;
      }
    },
    setRenderedPageDef(state, action: PayloadAction<PageDef | null>) {
      state.renderedPageDef = action.payload;
    },
    resetFormData(state) {
      state.formData = {};
      state.fileDisplayNames = {};
    },
    clearResourcePage(state) {
      // Reset to initial state but keep urlPath
      const currentUrlPath = state.urlPath;
      Object.assign(state, initialState);
      state.urlPath = currentUrlPath;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getResourcePage.pending, (state) => {
        state.isRenderedConfigLoading = true;
      })
      .addCase(getResourcePage.fulfilled, (state, action) => {
        state.isRenderedConfigLoading = false;
        const { page, pageDef } = action.payload;

        if (page.page) {
          // Merge rendered blocks with original blocks
          const originalBlocks = (pageDef?.blocks ??
            []) as unknown as PageBlockDef[];
          const mergedBlocks = originalBlocks.map((originalBlock, index) => {
            const renderedBlock = page.page?.blocks?.[index];
            if (!renderedBlock) return originalBlock;

            // Deep merge logic for blocks
            return mergeBlocks(
              originalBlock,
              renderedBlock as Record<string, unknown>,
            );
          });

          state.renderedPageDef = {
            ...pageDef,
            type: page.page.type.toLowerCase() as "instance" | "list",
            label: page.page.title,
            path: page.page.path,
            navigation: page.page.navigation,
            blocks: mergedBlocks as unknown as PageDef["blocks"],
            actions: page.page.actions?.map((action) => ({
              ...action,
              isDisabled: action.isDisabled,
              isHidden: action.isHidden,
            })),
            filter: page.page.filter as PageDef["filter"],
          } as PageDef;
        }

        // Process navigation
        if (
          page.navigation.sidebar?.sections &&
          page.navigation.sidebar.sections.length > 0
        ) {
          state.pageNavigation = page.navigation;
        } else {
          state.pageNavigation = null;
        }
      })
      .addCase(getResourcePage.rejected, (state) => {
        state.isRenderedConfigLoading = false;
        state.fetchCacheKey = null;
      })
      .addCase(getRelationshipOptions.fulfilled, (state, action) => {
        state.relationshipOptions = action.payload;
      })
      .addCase(getListBlockInstances.fulfilled, (state, action) => {
        state.listBlockInstances = action.payload;
      })
      .addCase(fetchCrmMapOverview.pending, (state, action) => {
        const installId = action.meta.arg.appInstallationId;
        state.panels.crmMapOverview = {
          ...state.panels.crmMapOverview,
          status:
            state.panels.crmMapOverview.data != null ? "loading" : "loading",
          error: null,
          installId,
        };
      })
      .addCase(fetchCrmMapOverview.fulfilled, (state, action) => {
        state.panels.crmMapOverview = {
          data: action.payload.data,
          status: "succeeded",
          error: null,
          installId: action.payload.installId,
        };
        state.panelRefreshKey += 1;
      })
      .addCase(fetchCrmMapOverview.rejected, (state, action) => {
        state.panels.crmMapOverview = {
          ...state.panels.crmMapOverview,
          status: "failed",
          error: action.error.message ?? "Failed to load CRM map overview",
        };
      })
      .addCase(fetchEventWiringOverview.pending, (state, action) => {
        const key = [
          action.meta.arg.appInstallationId,
          [...action.meta.arg.eventTypes].sort().join(","),
          action.meta.arg.recommendedWorkflowHandle ?? "",
        ].join("|");
        const prev = state.panels.eventWiringByKey[key] ?? emptyAsyncPanel();
        state.panels.eventWiringByKey[key] = {
          ...prev,
          status: "loading",
          error: null,
          installId: action.meta.arg.appInstallationId,
        };
      })
      .addCase(fetchEventWiringOverview.fulfilled, (state, action) => {
        state.panels.eventWiringByKey[action.payload.key] = {
          data: action.payload.data,
          status: "succeeded",
          error: null,
          installId: action.payload.installId,
        };
        state.panelRefreshKey += 1;
      })
      .addCase(fetchEventWiringOverview.rejected, (state, action) => {
        const key = [
          action.meta.arg.appInstallationId,
          [...action.meta.arg.eventTypes].sort().join(","),
          action.meta.arg.recommendedWorkflowHandle ?? "",
        ].join("|");
        const prev = state.panels.eventWiringByKey[key] ?? emptyAsyncPanel();
        state.panels.eventWiringByKey[key] = {
          ...prev,
          status: "failed",
          error: action.error.message ?? "Failed to load event wiring",
        };
      })
      .addCase(fetchInstallSetupSteps.pending, (state, action) => {
        state.panels.installSetup = {
          ...state.panels.installSetup,
          status: "loading",
          error: null,
          installId: action.meta.arg.appInstallationId,
        };
      })
      .addCase(fetchInstallSetupSteps.fulfilled, (state, action) => {
        state.panels.installSetup = {
          data: action.payload.data,
          status: "succeeded",
          error: null,
          installId: action.payload.installId,
        };
        state.panelRefreshKey += 1;
      })
      .addCase(fetchInstallSetupSteps.rejected, (state, action) => {
        state.panels.installSetup = {
          ...state.panels.installSetup,
          status: "failed",
          error: action.error.message ?? "Failed to load setup steps",
        };
      })
      .addCase(fetchCrmSetupStatus.pending, (state, action) => {
        state.panels.crmSetupStatus = {
          ...state.panels.crmSetupStatus,
          status: "loading",
          error: null,
          installId: action.meta.arg.appInstallationId,
        };
      })
      .addCase(fetchCrmSetupStatus.fulfilled, (state, action) => {
        state.panels.crmSetupStatus = {
          data: action.payload.data,
          status: "succeeded",
          error: null,
          installId: action.payload.installId,
        };
        state.panelRefreshKey += 1;
      })
      .addCase(fetchCrmSetupStatus.rejected, (state, action) => {
        state.panels.crmSetupStatus = {
          ...state.panels.crmSetupStatus,
          status: "failed",
          error: action.error.message ?? "Failed to load CRM setup status",
        };
      })
      .addCase(fetchLatestBatchJob.pending, (state, action) => {
        const key = [
          action.meta.arg.appInstallationId,
          action.meta.arg.entity,
          action.meta.arg.operationHandle,
        ].join("|");
        const prev = state.panels.batchJobsByKey[key] ?? {
          ...emptyAsyncPanel<null>(),
          crmConfigured: null,
          entity: action.meta.arg.entity,
          operationHandle: action.meta.arg.operationHandle,
        };
        state.panels.batchJobsByKey[key] = {
          ...prev,
          status: "loading",
          error: null,
          installId: action.meta.arg.appInstallationId,
          entity: action.meta.arg.entity,
          operationHandle: action.meta.arg.operationHandle,
        };
      })
      .addCase(fetchLatestBatchJob.fulfilled, (state, action) => {
        state.panels.batchJobsByKey[action.payload.key] = {
          data: action.payload.job,
          status: "succeeded",
          error: null,
          installId: action.payload.installId,
          crmConfigured: action.payload.crmConfigured,
          entity: action.payload.entity,
          operationHandle: action.payload.operationHandle,
        };
        state.panelRefreshKey += 1;
      })
      .addCase(fetchLatestBatchJob.rejected, (state, action) => {
        const key = [
          action.meta.arg.appInstallationId,
          action.meta.arg.entity,
          action.meta.arg.operationHandle,
        ].join("|");
        const prev = state.panels.batchJobsByKey[key] ?? {
          ...emptyAsyncPanel<null>(),
          crmConfigured: null,
          entity: action.meta.arg.entity,
          operationHandle: action.meta.arg.operationHandle,
        };
        state.panels.batchJobsByKey[key] = {
          ...prev,
          status: "failed",
          error: action.error.message ?? "Failed to load batch job",
        };
      })
      .addCase(refreshInstallPanels.fulfilled, (state) => {
        state.panelRefreshKey += 1;
      });
  },
});

function mergeBlocks(
  originalBlock: PageBlockDef,
  renderedBlock: Record<string, unknown>,
): PageBlockDef {
  return mergeRenderedPageBlock(originalBlock, renderedBlock);
}

// ─────────────────────────────────────────────────────────────────────────────
// Exports
// ─────────────────────────────────────────────────────────────────────────────

export const {
  setUrlPath,
  setPageDef,
  updateFormData,
  updateFormField,
  setFileDisplayName,
  setRelationshipOptions,
  setListBlockInstances,
  setPageNavigation,
  setFetchCacheKey,
  setIsRenderedConfigLoading,
  setActionLoadingState,
  bumpPanelRefreshKey,
  clearInstallPanels,
  applyBatchJobRealtimeUpdate,
  setRenderedPageDef,
  resetFormData,
  clearResourcePage,
} = appResourcePageSlice.actions;

// Selectors
export const selectUrlPath = (state: RootState) =>
  state.appResourcePage.urlPath;
export const selectPageDef = (state: RootState) =>
  state.appResourcePage.pageDef;
export const selectPathParams = (state: RootState) =>
  state.appResourcePage.pathParams;
export const selectModelDef = (state: RootState) =>
  state.appResourcePage.modelDef;
export const selectRenderedPageDef = (state: RootState) =>
  state.appResourcePage.renderedPageDef;
export const selectFormData = (state: RootState) =>
  state.appResourcePage.formData;
export const selectFileDisplayNames = (state: RootState) =>
  state.appResourcePage.fileDisplayNames;
export const selectRelationshipOptions = (state: RootState) =>
  state.appResourcePage.relationshipOptions;
export const selectListBlockInstances = (state: RootState) =>
  state.appResourcePage.listBlockInstances;
export const selectPageNavigation = (state: RootState) =>
  state.appResourcePage.pageNavigation;
export const selectIsLoading = (state: RootState) =>
  state.appResourcePage.isLoading;
export const selectIsRenderedConfigLoading = (state: RootState) =>
  state.appResourcePage.isRenderedConfigLoading;
export const selectActionLoadingStates = (state: RootState) =>
  state.appResourcePage.actionLoadingStates;
export const selectPanelRefreshKey = (state: RootState) =>
  state.appResourcePage.panelRefreshKey;
export const selectInstallPanels = (state: RootState) =>
  state.appResourcePage.panels;
export const selectCrmMapOverview = (state: RootState) =>
  state.appResourcePage.panels.crmMapOverview;
export const selectInstallSetupPanel = (state: RootState) =>
  state.appResourcePage.panels.installSetup;
export const selectCrmSetupStatusPanel = (state: RootState) =>
  state.appResourcePage.panels.crmSetupStatus;
export const selectFetchCacheKey = (state: RootState) =>
  state.appResourcePage.fetchCacheKey;

export default appResourcePageSlice.reducer;
